import { type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../../utils.js'

export interface SidebarItem {
  /** Unique identifier */
  id: string
  /** Label text */
  label: string
  /** Icon element */
  icon?: ReactNode
  /** Whether this item is active */
  active?: boolean
  /** Click handler */
  onClick?: () => void
}

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  /** Navigation items */
  items: SidebarItem[]
  /** Whether the sidebar is collapsed (icon-only mode) */
  collapsed?: boolean
  /** Called when collapse state changes */
  onCollapseChange?: (collapsed: boolean) => void
  /** Header/brand element */
  header?: ReactNode
  /** Footer element */
  footer?: ReactNode
}

/**
 * Collapsible sidebar navigation with icons and labels.
 * Shows as full drawer on mobile when toggled.
 */
export function Sidebar({
  items,
  collapsed = false,
  onCollapseChange,
  header,
  footer,
  className,
  ...props
}: SidebarProps) {
  return (
    <nav
      aria-label="Main navigation"
      className={cn('flex flex-col h-full', className)}
      {...props}
    >
      {/* Header */}
      {header && (
        <div className="px-3 py-4 border-b border-[var(--color-border)]">{header}</div>
      )}

      {/* Navigation items */}
      <div className="flex-1 overflow-y-auto py-2">
        <ul className="flex flex-col gap-0.5 px-2" role="list">
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={item.onClick}
                aria-current={item.active ? 'page' : undefined}
                className={cn(
                  'flex items-center gap-3 w-full rounded-[var(--radius-md)] px-3 py-2 text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
                  item.active
                    ? 'bg-[var(--color-primary-pale)] text-[var(--color-primary)] font-medium border-l-2 border-[var(--color-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]',
                  collapsed && 'justify-center',
                )}
                title={collapsed ? item.label : undefined}
              >
                {item.icon && (
                  <span className="shrink-0 h-5 w-5">{item.icon}</span>
                )}
                {!collapsed && <span>{item.label}</span>}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Footer */}
      {footer && (
        <div className="px-3 py-3 border-t border-[var(--color-border)]">{footer}</div>
      )}

      {/* Collapse toggle - always at the very bottom */}
      {onCollapseChange && (
        <div className="px-2 py-2 border-t border-[var(--color-border)]">
          <button
            type="button"
            onClick={() => onCollapseChange(!collapsed)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand' : undefined}
            className={cn(
              'group flex items-center gap-2.5 w-full rounded-[var(--radius-md)] px-3 py-2 text-sm',
              'text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] hover:text-[var(--color-text)]',
              'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]',
              collapsed && 'justify-center',
            )}
          >
            {/* Sidebar panel icon */}
            <svg
              className="h-4 w-4 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <line x1="9" y1="3" x2="9" y2="21" />
              {collapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 10l3 2-3 2" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 10l-3 2 3 2" />
              )}
            </svg>
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      )}
    </nav>
  )
}

/**
 * Mobile sidebar drawer overlay.
 */
export interface MobileSidebarProps {
  open: boolean
  onClose: () => void
  children: ReactNode
}

export function MobileSidebar({ open, onClose, children }: MobileSidebarProps) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] md:hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      {/* Drawer */}
      <div className="relative w-64 h-full bg-[var(--color-surface)] border-r border-[var(--color-border)] shadow-[var(--shadow-lg)]">
        {children}
      </div>
    </div>
  )
}
